import { useAppDispatch, useAppSelector } from "../../util/hooks"
import { theme as baseTheme } from "../../util/CustomTheme"
import { ALERT_MSGS, TOAST_OPTS } from "../../util/StaticData"
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tab,
  Tabs,
  ThemeProvider,
  createTheme,
  styled,
} from "@mui/material"
import { theme } from "../../util/theme"
import CloseIcon from "@mui/icons-material/Close"
import {
  closeDefaultConfigDialog,
  maintenancePipeLineBaseDataAsync,
  maintenancePipeLineUpdateBaseDataAsync,
  newPipelineCellChange,
  updateTabIndex,
} from "./MaintenanceSlice"
import {
  INewPipeLineTab,
  INewTableCellChangePayload,
} from "../../util/interface"
import NewPipeLineConfigTable from "../../components/NewPipeLineConfigTable"
import { useEffect } from "react"
// import { redThemedTabs } from "../Monitor/NewPipeline/NewPipeLineTableWrapper"
import { createUseStyles } from "react-jss"
import {
  findIndexByKey,
  inputSeriesDateConversionBeforeSave,
} from "../../util/CommonMethods"
import { toast } from "react-toastify"
import { redThemedTabs } from "../Monitor/NewPipeLineRun/NewPipeLineTableWrapper"
import {
  newPipeLineMapper,
  NewPipeLineTabs,
} from "../../util/NewPipelineConfig"

const BootstrapDialog = styled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}))

export const themeMui = createTheme({
  ...baseTheme,
  components: {
    MuiInputBase: {
      styleOverrides: {
        root: {
          fontSize: 14,
          height: 31,
        },
      },
    },
    MuiFormControlLabel: {
      styleOverrides: {
        root: {
          color: theme.dayLabelColor,
          fontWeight: 500,
          fontSize: 14.57,
        },
      },
    },
  },
})
const useStyles = createUseStyles({
  nextBtn: {
    borderRadius: "7px",
    background: "var(--primary-dp-color)",
    height: "27px",
    width: "fit-content",
    paddingLeft: 10,
    paddingRight: 10,
    color: "#FFF",
    fontFamily: "Roboto",
    fontSize: "12px",
    fontStyle: "normal",
    fontWeight: 700,
    lineHeight: "normal",
  },
  backBtn: {
    borderRadius: "7px",
    height: "27px",
    width: "66px",
    color: "black",
    fontSize: "12px",
    fontStyle: "normal",
    lineHeight: "normal",
    textDecoration: "underline",
  },
  bottomWrapper: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    alignItems: "center",
    width: "100%",
  },
  btnWrapper: {
    display: "flex",
    justifyContent: "flex-end",
  },
})
export const DefaultConfig = () => {
  const classes = useStyles({ theme }) // Custom styles using JSS
  const dispatch = useAppDispatch() // Hook to get the dispatch function
  const isModalOpen = useAppSelector(
    (store) => store.maintenance.isDefaultConfigModelOpen, // Select modal open state from the Redux store
  )
  const data = useAppSelector(
    (store) => store.maintenance.pipelineConfig.activeData, // Select active data for pipeline configuration
  )

  // Function to handle cell change events in the table
  const cellChange = (payload: INewTableCellChangePayload) => {
    dispatch(newPipelineCellChange(payload)) // Dispatch cell change action
  }

  // Function to handle modal close events
  const handleClose = (event: any, reason: string) => {
    // Prevent closing on backdrop click or escape key press
    if (reason && reason === "backdropClick" && "escapeKeyDown") return
    dispatch(closeDefaultConfigDialog()) // Dispatch close modal action
  }

  const allConfigData = useAppSelector(
    (state) => state.maintenance.pipelineConfig.tableData, // Select all configuration data
  )
  const useCaseSelectedValue = useAppSelector(
    (store) => store.filters.useCaseSelectedValue, // Select the currently selected use case
  )
  const marketSingleCaseSelectedValue = useAppSelector(
    (store) => store.filters.marketSingleCaseSelectedValue, // Select the currently selected market case
  )
  const activeTab = useAppSelector(
    (store) => store.maintenance.pipelineConfig.tabIndex, // Select the currently active tab index
  )

  // Function to handle tab change events
  const handleTabChange = (event: any, newValue: string) => {
    dispatch(updateTabIndex(newValue)) // Dispatch action to update the active tab index
  }

  const userChangedTabs = useAppSelector(
    (state) => state.maintenance.pipelineConfig.userChangedTabs, // Select user-changed tabs from the state
  )

  // Effect to fetch pipeline base data if necessary
  useEffect(() => {
    if (
      !allConfigData[activeTab].length &&
      useCaseSelectedValue &&
      marketSingleCaseSelectedValue
    ) {
      dispatch(maintenancePipeLineBaseDataAsync()) // Fetch pipeline base data
    }
  }, [activeTab, dispatch, useCaseSelectedValue, marketSingleCaseSelectedValue])

  const status = useAppSelector(
    (state) => state.maintenance.pipelineConfig.status, // Select the current status of the pipeline configuration
  )

  // Function to handle screen change for next or save action
  const handleScreenChange = async () => {
    const index = findIndexByKey(activeTab) // Find index of the active tab

    if (index === NewPipeLineTabs.length - 1) {
      // If on the last tab
      const tabKeys = Object.keys(allConfigData) // Get keys of all configuration data
      tabKeys.map((key) => {
        if (
          allConfigData[key] &&
          allConfigData[key].length &&
          userChangedTabs.includes(key)
        ) {
          let inputSeriesTempData
          // Prepare input series data for saving, if applicable
          if (key === "Input Series Config") {
            inputSeriesTempData = inputSeriesDateConversionBeforeSave(
              allConfigData[key],
            )
          } else {
            inputSeriesTempData = null
          }
          // Dispatch update action with prepared data
          dispatch(
            maintenancePipeLineUpdateBaseDataAsync({
              data: inputSeriesTempData
                ? inputSeriesTempData
                : allConfigData[key],
              fileType: newPipeLineMapper[key],
            }),
          )
            .then(() => {
              toast.success(`${key} updated successfully!`, TOAST_OPTS) // Show success toast
            })
            .catch(() => {
              toast.error(
                `${key} ${ALERT_MSGS.PIPELINE.CONFIG_UPDATE_FAILED}`, // Show error toast on failure
                TOAST_OPTS,
              )
            })
        }
        return ""
      })
      dispatch(closeDefaultConfigDialog()) // Close the dialog after processing all tabs
    } else {
      dispatch(updateTabIndex(NewPipeLineTabs[index + 1].key)) // Move to the next tab
    }
  }

  // Function to handle back navigation
  const handleBack = () => {
    const index = findIndexByKey(activeTab) // Find the index of the active tab
    if (index !== 0) {
      // If not on the first tab
      dispatch(updateTabIndex(NewPipeLineTabs[index - 1].key)) // Move to the previous tab
    }
  }

  // Render the component
  return (
    <BootstrapDialog
      onClose={handleClose} // Handle modal close
      aria-labelledby="default-config-dialog"
      open={isModalOpen} // Open state of the modal
      fullWidth={true}
      maxWidth="md" // Set modal width
    >
      <DialogTitle sx={{ m: 0, p: 1 }} id="default-config-dialog">
        Set Configurations to Default
        <IconButton
          aria-label="close" // Button to close the dialog
          onClick={(e) => handleClose(e, "close")}
          sx={{
            position: "absolute",
            right: 20,
            zIndex: 100,
            top: 10,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <ThemeProvider theme={redThemedTabs}>
          <Tabs
            value={activeTab} // Current tab value
            onChange={handleTabChange} // Handle tab change
            textColor="primary"
          >
            {NewPipeLineTabs.map((obj: INewPipeLineTab, index: number) => {
              return (
                <Tab
                  sx={{
                    textTransform: "none", // Disable text transform for tab labels
                  }}
                  value={obj.label}
                  label={obj.label}
                  key={index} // Unique key for each tab
                />
              )
            })}
          </Tabs>
        </ThemeProvider>

        {status === "loading" ? ( // Show loading spinner if data is loading
          <Box
            sx={{
              minHeight: 500,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : (
          <NewPipeLineConfigTable
            activeTab={activeTab} // Pass active tab to table
            cellChange={cellChange} // Pass cell change handler
            data={data} // Pass active data to table
          />
        )}
      </DialogContent>
      <DialogActions>
        <div className={classes.bottomWrapper}>
          <div></div>

          <div className={classes.btnWrapper}>
            <button className={classes.backBtn} onClick={handleBack}>
              Back
            </button>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <button className={classes.nextBtn} onClick={handleScreenChange}>
                {NewPipeLineTabs.findIndex((obj) => obj.key === activeTab) ===
                NewPipeLineTabs.length - 1
                  ? "Save" // Button text changes to "Save" on the last tab
                  : "Next"}
              </button>
            </Box>
          </div>
        </div>
      </DialogActions>
    </BootstrapDialog>
  )
}
